<template>
  <div class="custom-menu">
    <div class="menu-container">
      <div 
        v-for="menu in menus" 
        :key="menu.path" 
        class="menu-item"
        @mouseenter="handleMouseEnter(menu)"
        @mouseleave="handleMouseLeave"
        @click="handleMenuClick(menu)"
      >
        <div class="menu-title">
          <span>{{ menu.meta?.title }}</span>
          <svg
            v-if="menu.children && menu.children.length > 0"
            class="menu-icon"
            :class="{ 'icon-rotate': hoveredMenu === menu.path }"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <g opacity="0.75">
              <rect transform="matrix(1, 0, 0, -1, 0, 12)" x="0" y="0" width="12" height="12" style="mix-blend-mode:normal" fill="#000000"></rect>
              <path d="M6.63582 3.222C6.31139 2.89757 5.80088 2.87261 5.44782 3.14713L5.36303 3.222L1.5093 7.07574C1.27498 7.31005 1.27498 7.68995 1.5093 7.92426C1.72018 8.13515 2.04899 8.15623 2.28345 7.98753L2.35783 7.92426L6.00048 4.2823L9.64103 7.92426C9.85191 8.13515 10.1807 8.15624 10.4152 7.98753L10.4896 7.92426C10.7004 7.71338 10.7215 7.38457 10.5528 7.15011L10.4896 7.07574L6.63582 3.222Z" style="mix-blend-mode:normal" fill="#FFFFFF"></path>
            </g>
          </svg>
        </div>
        
        <!-- 二级菜单 -->
        <Transition name="submenu">
          <div 
            v-if="menu.children && menu.children.length > 0 && hoveredMenu === menu.path"
            class="submenu-container"
            :class="`submenu-align-${submenuAlign}`"
          >
            <div class="submenu-background">
              <div 
                v-for="child in menu.children"
                :key="child.path"
                class="submenu-item"
                @click.stop="handleSubmenuClick(child)"
              >
                <span class="submenu-text">{{ child.meta?.title }}</span>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  menus: {
    type: Array,
    default: () => []
  },
  activeIndex: {
    type: String,
    default: ''
  },
  submenuAlign: {
    type: String,
    default: 'center', // 'left', 'center', 'right'
    validator: (value) => ['left', 'center', 'right'].includes(value)
  }
})

const emit = defineEmits(['select'])

const hoveredMenu = ref('')

const handleMouseEnter = (menu) => {
  if (menu.children && menu.children.length > 0) {
    hoveredMenu.value = menu.path
  }
}

const handleMouseLeave = () => {
  hoveredMenu.value = ''
}

const handleMenuClick = (menu) => {
  if (!menu.children || menu.children.length === 0) {
    emit('select', menu.path)
  }
}

const handleSubmenuClick = (child) => {
  emit('select', child.path)
  hoveredMenu.value = ''
}
</script>

<style scoped lang="scss">
.custom-menu {
  position: relative;
  height: 60px;
  display: flex;
  align-items: center;
}

.menu-container {
  display: flex;
  align-items: center;
  height: 100%;
  white-space: nowrap; /* 防止换行 */

}

.menu-item {
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;
    cursor: pointer;
    transition: all 0.3s ease;
  }

.menu-title {
    display: flex;
    align-items: center;
    gap: 4px;
    
    span {
      font-size: 16px;
      font-weight: 400;
      letter-spacing: 0px;
      line-height: 24px;
      color: rgba(255, 255, 255, 1);
      text-align: left;
      vertical-align: top;
    }
  }

.menu-icon {
  color: rgba(255, 255, 255, 1);
  font-size: 12px;
  transition: transform 0.3s ease;
  transform: rotate(180deg); /* 默认箭头向下 */
  
  &.icon-rotate {
    transform: rotate(0deg); /* hover时箭头向上 */
  }
}

.submenu-container {
    position: absolute;
    top: 100%;
    z-index: 1000;
    margin-top: 8px;
    
    /* 添加透明背景层覆盖8px间距，防止鼠标事件中断 */
    &::before {
      content: '';
      position: absolute;
      top: -8px;
      left: 0;
      width: 100%;
      height: 8px;
      background: transparent;
    }
    
    &.submenu-align-left {
      left: 0;
    }
    
    &.submenu-align-center {
      left: 50%;
      transform: translateX(-50%);
    }
    
    &.submenu-align-right {
      right: 0;
    }
  }

  .submenu-background {
    width: 130px;
    min-height: 96px;
    opacity: 1;
    border-radius: 8px;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0px 2px 16px rgba(0, 0, 0, 0.15);
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

.submenu-item {
  width: 114px;
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 1);
  
  &:hover {
    background: rgba(247, 247, 247, 1);
    .submenu-text {
      font-size: 16px;
      font-weight: 500;
      letter-spacing: 0px;
      line-height: 24px;
      color: rgba(70, 95, 253, 1);
      text-align: center; /* 悬停时文字也居中对齐 */
      vertical-align: top;
    }
  }
}

.submenu-text {
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0px;
  line-height: 24px;
  color: rgba(51, 51, 51, 1);
  text-align: center; /* 文字居中对齐 */
  vertical-align: top;
  transition: all 0.3s ease;
  width: 100%; /* 确保文字占满整个容器宽度以实现居中效果 */
}

/* 子菜单过渡动画 */
.submenu-enter-active,
.submenu-leave-active {
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
}

.submenu-align-left {
  .submenu-enter-active,
  .submenu-leave-active {
    transform-origin: 0 0;
  }
}

.submenu-align-center {
  .submenu-enter-active,
  .submenu-leave-active {
    transform-origin: 50% 0;
  }
}

.submenu-align-right {
  .submenu-enter-active,
  .submenu-leave-active {
    transform-origin: 100% 0;
  }
}

.submenu-enter-from {
  opacity: 0;
  transform: scaleY(0.8) translateY(-10px);
}

.submenu-leave-to {
  opacity: 0;
  transform: scaleY(0.8) translateY(-10px);
}
</style>